/**
 * Enhanced Authentication Utilities for Supabase Golden Path
 * 
 * Provides multi-tenant authentication with automatic org_id assignment
 * and JWT claims enrichment for RLS policies.
 */

import { supabase, handleAuthError } from "./supabaseClient";

export interface AuthUser {
  id: string;
  email: string;
  org_id?: string;
  role?: string;
  stream?: string;
  display_name?: string;
}

export interface SignupOptions {
  email: string;
  password: string;
  display_name?: string;
  stream?: string;
  role?: string;
  org_name?: string;
  metadata?: Record<string, any>;
}

export interface SigninOptions {
  email: string;
  password: string;
}

/**
 * Enhanced signup with automatic org_id assignment
 */
export const signUpWithOrg = async (options: SignupOptions) => {
  try {
    console.log("🔍 Auth: Starting enhanced signup with org assignment...");

    // Step 1: Create or get organization
    let orgId: string;
    
    if (options.org_name) {
      // Create new organization
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .insert({
          name: options.org_name,
          slug: options.org_name.toLowerCase().replace(/\s+/g, '-'),
        })
        .select("id")
        .single();

      if (orgError) {
        console.error("❌ Auth: Error creating organization:", orgError);
        throw new Error("Failed to create organization");
      }
      
      orgId = orgData.id;
      console.log("✅ Auth: Created organization:", orgId);
    } else {
      // Use default organization for individual users
      const { data: defaultOrg } = await supabase
        .from("organizations")
        .select("id")
        .eq("name", "Acme Inc")
        .single();

      if (!defaultOrg) {
        throw new Error("Default organization not found");
      }
      
      orgId = defaultOrg.id;
      console.log("✅ Auth: Using default organization:", orgId);
    }

    // Step 2: Sign up user with enriched metadata
    const { data, error } = await supabase.auth.signUp({
      email: options.email,
      password: options.password,
      options: {
        data: {
          org_id: orgId,
          role: options.role || "member",
          stream: options.stream || "individual",
          display_name: options.display_name,
          ...options.metadata,
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      console.error("❌ Auth: Signup error:", error);
      throw new Error(handleAuthError(error, options.email));
    }

    // Step 3: Create profile with org_id
    if (data.user) {
      const { error: profileError } = await supabase
        .from("profiles")
        .insert({
          id: data.user.id,
          org_id: orgId,
          email: options.email,
          display_name: options.display_name,
          role: options.role || "member",
          stream: options.stream || "individual",
        });

      if (profileError) {
        console.error("❌ Auth: Profile creation error:", profileError);
        // Don't throw here - user is created, profile can be fixed later
      } else {
        console.log("✅ Auth: Profile created successfully");
      }
    }

    console.log("✅ Auth: Enhanced signup completed");
    return { data, error: null };

  } catch (error) {
    console.error("❌ Auth: Enhanced signup failed:", error);
    return { data: null, error };
  }
};

/**
 * Enhanced signin with org_id validation
 */
export const signInWithOrg = async (options: SigninOptions) => {
  try {
    console.log("🔍 Auth: Starting enhanced signin...");

    const { data, error } = await supabase.auth.signInWithPassword({
      email: options.email,
      password: options.password,
    });

    if (error) {
      console.error("❌ Auth: Signin error:", error);
      throw new Error(handleAuthError(error, options.email));
    }

    // Validate user has org_id
    if (data.user) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id, role, stream")
        .eq("id", data.user.id)
        .single();

      if (!profile?.org_id) {
        console.warn("⚠️ Auth: User missing org_id, assigning default");
        // Assign default organization
        const { data: defaultOrg } = await supabase
          .from("organizations")
          .select("id")
          .eq("name", "Acme Inc")
          .single();

        if (defaultOrg) {
          await supabase
            .from("profiles")
            .update({ org_id: defaultOrg.id })
            .eq("id", data.user.id);
        }
      }
    }

    console.log("✅ Auth: Enhanced signin completed");
    return { data, error: null };

  } catch (error) {
    console.error("❌ Auth: Enhanced signin failed:", error);
    return { data: null, error };
  }
};

/**
 * Get current user with org context
 */
export const getCurrentUserWithOrg = async (): Promise<AuthUser | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return null;

    const { data: profile } = await supabase
      .from("profiles")
      .select("org_id, role, stream, display_name")
      .eq("id", user.id)
      .single();

    return {
      id: user.id,
      email: user.email || "",
      org_id: profile?.org_id,
      role: profile?.role,
      stream: profile?.stream,
      display_name: profile?.display_name,
    };
  } catch (error) {
    console.error("❌ Auth: Error getting current user:", error);
    return null;
  }
};

/**
 * Update user profile with org context
 */
export const updateUserProfileWithOrg = async (
  userId: string,
  updates: {
    display_name?: string;
    role?: string;
    stream?: string;
    org_id?: string;
  }
) => {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) {
      console.error("❌ Auth: Profile update error:", error);
      throw error;
    }

    // Update auth user metadata if needed
    if (updates.display_name) {
      await supabase.auth.updateUser({
        data: { display_name: updates.display_name }
      });
    }

    return data;
  } catch (error) {
    console.error("❌ Auth: Profile update failed:", error);
    throw error;
  }
};

/**
 * Get organization details for current user
 */
export const getCurrentUserOrg = async () => {
  try {
    const user = await getCurrentUserWithOrg();
    if (!user?.org_id) return null;

    const { data, error } = await supabase
      .from("organizations")
      .select("*")
      .eq("id", user.org_id)
      .single();

    if (error) {
      console.error("❌ Auth: Error getting organization:", error);
      return null;
    }

    return data;
  } catch (error) {
    console.error("❌ Auth: Error getting user organization:", error);
    return null;
  }
};

/**
 * Switch user organization (admin function)
 */
export const switchUserOrg = async (userId: string, orgId: string) => {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .update({ org_id: orgId })
      .eq("id", userId)
      .select()
      .single();

    if (error) {
      console.error("❌ Auth: Error switching organization:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("❌ Auth: Organization switch failed:", error);
    throw error;
  }
};

/**
 * Legacy compatibility functions
 */
export const signInWithEmail = async (email: string) => {
  if (!email) {
    return { data: null, error: { message: "Email is required" } };
  }

  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) {
    console.error("❌ Auth: Magic link error:", error);
    return { data: null, error };
  }

  return { data, error: null };
};

export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
      queryParams: {
        access_type: "offline",
        prompt: "consent",
      },
    },
  });

  if (error) {
    console.error("❌ Auth: Google OAuth error:", error);
    throw error;
  }

  return { data, error: null };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error("❌ Auth: Signout error:", error);
    throw error;
  }
};

/**
 * Validate JWT claims for RLS
 */
export const validateJWTClaims = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      return { valid: false, error: "No active session" };
    }

    const user = await getCurrentUserWithOrg();
    
    if (!user?.org_id) {
      return { valid: false, error: "User missing org_id" };
    }

    return { 
      valid: true, 
      user,
      claims: {
        sub: user.id,
        org_id: user.org_id,
        role: user.role,
        stream: user.stream,
      }
    };
  } catch (error) {
    console.error("❌ Auth: JWT validation error:", error);
    return { valid: false, error: error.message };
  }
};

// Re-export supabase client for backward compatibility
// Legacy exports for backward compatibility
export const authService = {
  signUp: signUpWithOrg,
  signIn: signInWithOrg,
  signOut,
  getCurrentUser: getCurrentUserWithOrg,
  updateProfile: updateUserProfileWithOrg,
};

export const getSupabaseAsync = async () => supabase;

export const validateLoginPassword = (password: string): boolean => {
  return password.length >= 6;
};

export const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }
  
  if (!/\d/.test(password)) {
    errors.push("Password must contain at least one number");
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const handleOAuthCallback = async (provider: string) => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    
    if (data.session) {
      return { success: true, user: data.session.user };
    }
    
    return { success: false, error: "No session found" };
  } catch (error) {
    console.error("OAuth callback error:", error);
    return { success: false, error: error.message };
  }
};

export { supabase };
